import React, { useState } from 'react';
import { Upload, FileText, MoreHorizontal, Trash2, Download, Eye } from 'lucide-react';

const KnowledgeBasePage = () => {
  const [documents] = useState([
    {
      id: 1,
      name: 'FAQ.pdf',
      status: 'Active',
      size: '2.3 MB',
      updated: '2 days ago',
      type: 'pdf'
    },
    {
      id: 2,
      name: 'Product Catalog.docx',
      status: 'Processing',
      size: '5.1 MB',
      updated: '1 week ago',
      type: 'docx'
    },
    {
      id: 3,
      name: 'Support Guidelines.md',
      status: 'Active',
      size: '156 KB',
      updated: '3 days ago',
      type: 'md'
    }
  ]);

  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      // Handle file upload
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium";
    
    switch (status) {
      case 'Active':
        return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
      case 'Processing':
        return `${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      case 'Error':
        return `${baseClasses} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200`;
    }
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-primary mb-2">Knowledge Base</h1>
          <p className="text-secondary">Upload and manage documents to train your AI assistant</p>
        </div>
        <button className="button-primary flex items-center space-x-2">
          <Upload className="w-4 h-4" />
          <span>Upload</span>
        </button>
      </div>

      {/* Documents Table */}
      <div className="card mb-8">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="px-6 py-4 text-left text-sm font-medium text-secondary uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-secondary uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-secondary uppercase tracking-wider">
                  Size
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-secondary uppercase tracking-wider">
                  Updated
                </th>
                <th className="px-6 py-4 text-right text-sm font-medium text-secondary uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {documents.map((doc) => (
                <tr key={doc.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                        <FileText className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                      </div>
                      <span className="font-medium text-primary">{doc.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={getStatusBadge(doc.status)}>{doc.status}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-secondary">
                    {doc.size}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-secondary">
                    {doc.updated}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200">
                        <Eye className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      </button>
                      <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200">
                        <Download className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      </button>
                      <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200">
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                      <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200">
                        <MoreHorizontal className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upload Area */}
      <div className="card">
        <div className="card-content">
          <div
            className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors duration-200 ${
              dragActive
                ? 'border-blue-400 bg-blue-50 dark:bg-blue-950/20'
                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className="flex flex-col items-center space-y-4">
              <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-full">
                <Upload className="w-8 h-8 text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-primary mb-2">Upload documents</h3>
                <p className="text-secondary mb-4">Drag and drop files here, or click to browse</p>
                <p className="text-sm text-tertiary">Supported: PDF, DOC, DOCX, TXT, MD</p>
              </div>
              <button className="button-primary">
                Choose Files
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBasePage;